// Dial Lists index for v2.
//
// Phase 4 ports the legacy DialListsPage to the v2 shell layout: an
// Attio-style table of lists with name, visibility, updated date.
// Click a row → /dial/:id (legacy detail page for now; that has 439
// LOC of complex per-entry editing UI and gets its own port phase).
// Inline "+ New list" pops a small create form below the toolbar.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DialList } from '@personical/cc-shared-types';
import { FAB } from '../../app/FAB.js';
import { useApi } from '../../components/api-context.js';
import { useToast } from '../../toast.js';

export function ListsIndex() {
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();

  const [items, setItems] = useState<DialList[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'shared' | 'private'>('private');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .listDialLists()
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const created = await api.createDialList({ name: name.trim(), visibility });
      setItems((prev) => [created, ...(prev ?? [])]);
      setName('');
      setShowForm(false);
      toast.success(`List "${created.name}" created.`);
      navigate(`/dial/${created.id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="hidden md:flex items-center gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h1 className="text-xl font-semibold text-strong">Lists</h1>
          {items && (
            <span className="text-sm text-faint tabular-nums">
              {items.length} list{items.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <p className="text-xs text-muted flex-1 min-w-[12rem] max-w-md">
          Outbound calling campaigns — pick a list of contacts and work through them, logging the
          outcome on each.
        </p>
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          className="btn-primary px-3 py-1.5 text-sm font-medium"
        >
          {showForm ? 'Cancel' : 'New list'}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={create}
          className="surface border border-divider rounded-lg p-3 flex flex-wrap gap-2 items-end"
        >
          <label className="flex-1 min-w-[200px] block">
            <span className="text-xs uppercase tracking-wider text-faint">Name</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q1 follow-up calls"
              required
              className="input w-full mt-1"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-faint">Visibility</span>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as 'shared' | 'private')}
              className="select mt-1"
            >
              <option value="private">Private</option>
              <option value="shared">Shared</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={creating || !name.trim()}
            className="btn-primary px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create'}
          </button>
        </form>
      )}

      {error && (
        <p className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200">
          {error}
        </p>
      )}

      <div className="surface border border-divider rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted surface-muted">
            <tr>
              <th className="px-3 py-2.5 border-b border-divider font-medium">Name</th>
              <th className="px-3 py-2.5 border-b border-divider font-medium" style={{ width: '7rem' }}>
                Visibility
              </th>
              <th className="px-3 py-2.5 border-b border-divider font-medium" style={{ width: '10rem' }}>
                Updated
              </th>
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center text-muted">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center text-muted">
                  No lists yet. Create one above.
                </td>
              </tr>
            ) : (
              items.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => navigate(`/dial/${l.id}`)}
                  className="border-b border-divider last:border-b-0 cursor-pointer hover:surface-hover"
                >
                  <td className="px-3 py-2.5 align-middle">
                    <p className="font-medium text-strong truncate">{l.name}</p>
                    {l.description && (
                      <p className="text-xs text-muted mt-0.5 truncate">{l.description}</p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-middle">
                    {l.visibility === 'private' ? (
                      <span className="inline-flex items-center rounded-md surface-muted text-mid text-[11px] px-1.5 py-0.5 font-medium">
                        Private
                      </span>
                    ) : (
                      <span className="text-faint text-xs">Shared</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-middle text-mid tabular-nums">
                    {new Date(l.updatedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <FAB onClick={() => setShowForm(true)} label="New list" />
    </div>
  );
}
